/**
 * Wrap-to-identity: NaCl `box` (X25519 + XSalsa20-Poly1305) keyed by Ed25519 identities (Milestone 13 / roadmap §20.3).
 *
 * An author wraps a secret (e.g. a content-key / [Capability]) to a recipient using only the two parties' existing
 * Ed25519 identities — no separately distributed encryption key, no key directory. Each side's signing identity is
 * converted to its Curve25519 counterpart ([Curve25519.edToCurvePublic] / [edSeedToCurveSecret]); the shared key is
 * `crypto_box_beforenm` (X25519 + HSalsa20, via [SecretBox.beforeNmKey]); the secret is then sealed with [SecretBox].
 *
 * The wrap is **authenticated**, not anonymous: it binds the author's identity, so a wrap is an attributable act
 * (roadmap §14.3) — admission = author a wrap to a member, removal = stop authoring wraps to it, retroactive read = an
 * explicit attributable re-wrap of an old key. A custodian may hold the wrap bytes without being able to open them
 * (reader/custodian split): opening requires the recipient's private key.
 */
package com.oasisfeng.invaluable.identity

object IdentityBox {

    /** Tag-prefixed output is `tag(16) || ciphertext`, the NaCl `box` wire form. */
    const val OVERHEAD = SecretBox.TAG_SIZE
    const val NONCE_SIZE = SecretBox.NONCE_SIZE

    private val SPKI_PREFIX = Codec.hexDecode("302a300506032b6570032100")

    /** Wrap [secret] to [recipientPublicKeySpki], authored by [authorPrivateKeyBytes]; returns `tag || ciphertext`. */
    fun wrap(secret: ByteArray, recipientPublicKeySpki: ByteArray, authorPrivateKeyBytes: ByteArray, nonce: ByteArray): ByteArray =
        SecretBox.seal(sharedKey(recipientPublicKeySpki, authorPrivateKeyBytes), nonce, secret)

    /** Open a wrap authored by [authorPublicKeySpki] addressed to us ([recipientPrivateKeyBytes]); null if it is not ours. */
    fun unwrap(boxed: ByteArray, authorPublicKeySpki: ByteArray, recipientPrivateKeyBytes: ByteArray, nonce: ByteArray): ByteArray? =
        SecretBox.open(sharedKey(authorPublicKeySpki, recipientPrivateKeyBytes), nonce, boxed)

    /** `crypto_box_beforenm` between a peer's public identity and our private identity — symmetric in author/recipient. */
    private fun sharedKey(peerPublicKeySpki: ByteArray, ownPrivateKeyBytes: ByteArray): ByteArray {
        val peerCurvePublic = Curve25519.edToCurvePublic(edPublicRaw(peerPublicKeySpki))
        val ownCurveSecret = Curve25519.edSeedToCurveSecret(Ed25519.seed(ownPrivateKeyBytes))
        return SecretBox.beforeNmKey(Curve25519.scalarMult(ownCurveSecret, peerCurvePublic))
    }

    /** The 32 raw Ed25519 public-key bytes carried in an X.509 SPKI encoding (identical on every platform). */
    private fun edPublicRaw(spki: ByteArray): ByteArray {
        require(spki.size == SPKI_PREFIX.size + 32) { "Unexpected Ed25519 SPKI length: ${spki.size}" }
        for (i in SPKI_PREFIX.indices) require(spki[i] == SPKI_PREFIX[i]) { "Unexpected Ed25519 SPKI prefix." }
        return spki.copyOfRange(SPKI_PREFIX.size, spki.size)
    }
}
